import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Loader2, Mail, CheckCircle2, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LanguageSwitcher } from "../components/app/LanguageSwitcher";
import { useT } from "../context/LanguageContext";
import api from "../lib/api";

export default function ForgotPassword() {
  const { t } = useT();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Guard against rapid double-submit: React 18 batches state updates so the
  // button's disabled prop doesn't take effect until the next render. A
  // millisecond-fast double-click would otherwise issue two forgot-password
  // calls and (under the old backend behavior) kill the user's first link.
  const inFlight = useRef(false);

  const submit = async (e) => {
    e.preventDefault();
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    try {
      // Backend always returns 200 with a generic message — never reveals
      // whether the email exists. We mirror that by ignoring the response body.
      await api.post("/auth/forgot-password", { email: email.trim() });
    } catch {
      // Even on a transient network error we show the same generic success
      // state, to avoid user-enumeration via UI differences.
    } finally {
      setLoading(false);
      setSubmitted(true);
      inFlight.current = false;
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#f9f9f8]" data-testid="forgot-password-page">
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-green-800 to-green-900 p-12 text-white items-center">
        <div className="max-w-md">
          <Link to="/" className="inline-flex items-center gap-2">
            <span className="w-9 h-9 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center">
              <Sparkles className="w-4 h-4" />
            </span>
            <span className="font-display font-bold text-xl">Tasklio AI</span>
          </Link>
          <h2 className="font-display mt-16 text-4xl font-semibold tracking-tight leading-tight">
            {t("forgot.sidebarTitle")}
          </h2>
          <p className="mt-5 text-green-50 leading-relaxed">
            {t("forgot.sidebarBody")}
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-8">
            <Link to="/" className="lg:hidden inline-flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-green-800 text-white flex items-center justify-center">
                <Sparkles className="w-4 h-4" />
              </span>
              <span className="font-display font-bold text-lg">Tasklio AI</span>
            </Link>
            <div className="ml-auto">
              <LanguageSwitcher testidPrefix="forgot-lang-switch" />
            </div>
          </div>

          {!submitted ? (
            <>
              <h1 className="font-display text-3xl font-semibold text-gray-900" data-testid="forgot-password-title">
                {t("forgot.title")}
              </h1>
              <p className="mt-2 text-gray-500">
                {t("forgot.subtitle")}
              </p>

              <form onSubmit={submit} className="mt-8 space-y-5">
                <div>
                  <Label htmlFor="email">{t("auth.email")}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("auth.emailPlaceholder")}
                    required
                    className="mt-2 h-11"
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 bg-green-800 hover:bg-green-900 text-white"
                  data-testid="forgot-submit-button"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("forgot.submit")}
                </Button>
              </form>

              <p className="mt-8 text-sm text-gray-500 text-center">
                <Link
                  to="/login"
                  className="text-green-800 hover:underline font-medium inline-flex items-center gap-1"
                  data-testid="forgot-back-to-login"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> {t("forgot.backToLogin")}
                </Link>
              </p>
            </>
          ) : (
            <div data-testid="forgot-success-state">
              <div className="w-12 h-12 rounded-full bg-green-100 text-green-700 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <h1 className="font-display mt-5 text-3xl font-semibold text-gray-900" data-testid="forgot-success-title">
                {t("forgot.successTitle")}
              </h1>
              <p className="mt-3 text-gray-600 leading-relaxed" data-testid="forgot-success-message">
                {t("forgot.successBody")}
              </p>
              <p className="mt-3 text-sm text-gray-500 leading-relaxed flex items-start gap-2">
                <Mail className="w-4 h-4 mt-0.5 shrink-0 text-gray-400" />
                <span>{t("forgot.spamHint")}</span>
              </p>
              <div className="mt-8 flex flex-col gap-2">
                <Link
                  to="/login"
                  className="w-full h-11 inline-flex items-center justify-center rounded-md bg-green-800 hover:bg-green-900 text-white font-medium transition-colors"
                  data-testid="forgot-success-back-to-login"
                >
                  {t("forgot.backToLogin")}
                </Link>
                <button
                  type="button"
                  onClick={() => { setSubmitted(false); setEmail(""); }}
                  className="text-sm text-gray-500 hover:text-gray-700 underline"
                  data-testid="forgot-resend-link"
                >
                  {t("forgot.tryAnother")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
